/**
 * <anja-looks-grid>: instant client-side filtering of look cards by sport and audience, live counts,
 * "show more" paging and shareable URL state (?sport=tennis&for=girls). All looks are server-rendered
 * (lazy images), so filtering never needs a network request.
 */
class AnjaLooksGrid extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #visible = 0;
  /** @type {Record<string, string>} */
  #state = { sport: '', audience: '' };

  connectedCallback() {
    this.list = this.querySelector('[data-anja-looks-list]');
    if (!this.list) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Merchant sort order (sort_order field), featured first; keeps DOM order = visual order for keyboards.
    this.cards = /** @type {HTMLElement[]} */ (Array.from(this.list.querySelectorAll('[data-anja-lookcard]')));
    this.cards
      .sort((a, b) => {
        const fa = a.dataset.featured === 'true' ? 0 : 1;
        const fb = b.dataset.featured === 'true' ? 0 : 1;
        return fa - fb || Number(a.dataset.sort) - Number(b.dataset.sort);
      })
      .forEach((card) => this.list?.append(card));

    this.step = Number(this.dataset.step) || 9;
    this.#visible = Number(this.dataset.initial) || 9;

    const params = new URLSearchParams(location.search);
    this.#state.sport = params.get('sport') || '';
    this.#state.audience = params.get('for') || '';

    this.querySelectorAll('[data-filter-group]').forEach((group) => {
      const name = /** @type {HTMLElement} */ (group).dataset.filterGroup || '';
      group.addEventListener(
        'click',
        (event) => {
          const pill = /** @type {HTMLElement} */ (event.target).closest('[data-filter-value]');
          if (!(pill instanceof HTMLElement)) return;
          this.#state[name] = pill.dataset.filterValue || '';
          this.#visible = Number(this.dataset.initial) || 9;
          this.#apply(true);
        },
        { signal }
      );
    });
    this.querySelector('[data-anja-looks-more] button')?.addEventListener(
      'click',
      () => {
        this.#visible += this.step;
        this.#apply(false, true);
      },
      { signal }
    );
    this.querySelector('[data-anja-looks-reset]')?.addEventListener(
      'click',
      () => {
        this.#state = { sport: '', audience: '' };
        this.#apply(true);
      },
      { signal }
    );
    this.#apply(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {HTMLElement} card */
  #matches(card, ignore = '') {
    const { sport, audience } = this.#state;
    if (ignore !== 'sport' && sport && card.dataset.sport !== sport) return false;
    if (ignore !== 'audience' && audience && card.dataset.audience !== audience && card.dataset.audience !== 'everyone') return false;
    return true;
  }

  /**
   * @param {boolean} updateUrl
   * @param {boolean} [animateNew]
   */
  #apply(updateUrl, animateNew = false) {
    const cards = this.cards || [];
    const matching = cards.filter((card) => this.#matches(card));
    cards.forEach((card) => {
      const index = matching.indexOf(card);
      const show = index !== -1 && index < this.#visible;
      const wasHidden = card.hidden || card.classList.contains('is-filtered');
      card.classList.toggle('is-filtered', index === -1);
      card.hidden = index !== -1 && index >= this.#visible;
      card.classList.toggle('is-entering', show && (animateNew || updateUrl) && wasHidden);
    });

    // Pressed state + counts
    this.querySelectorAll('[data-filter-group]').forEach((group) => {
      const name = /** @type {HTMLElement} */ (group).dataset.filterGroup || '';
      group.querySelectorAll('[data-filter-value]').forEach((pill) => {
        const value = /** @type {HTMLElement} */ (pill).dataset.filterValue || '';
        pill.setAttribute('aria-pressed', String(value === this.#state[name]));
        const count = pill.querySelector('[data-count]');
        if (count && value) {
          const n = cards.filter((c) => this.#matches(c, name) && /** @type {any} */ (c.dataset)[name] === value).length;
          count.textContent = String(n);
        }
      });
    });

    const counter = /** @type {HTMLElement | null} */ (this.querySelector('[data-anja-looks-count]'));
    if (counter) counter.textContent = (counter.dataset.template || '').replace('[count]', String(matching.length));
    const empty = /** @type {HTMLElement | null} */ (this.querySelector('[data-anja-looks-empty]'));
    if (empty) empty.hidden = matching.length > 0;
    const more = /** @type {HTMLElement | null} */ (this.querySelector('[data-anja-looks-more]'));
    if (more) more.hidden = matching.length <= this.#visible;

    if (updateUrl) {
      const url = new URL(location.href);
      this.#state.sport ? url.searchParams.set('sport', this.#state.sport) : url.searchParams.delete('sport');
      this.#state.audience ? url.searchParams.set('for', this.#state.audience) : url.searchParams.delete('for');
      history.replaceState(history.state, '', url);
    }
  }
}

if (!customElements.get('anja-looks-grid')) customElements.define('anja-looks-grid', AnjaLooksGrid);
